import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ProjectType } from '../../models/sow';
import { ChefHat, Bath, Home, Building, Wrench, ArrowRight, CheckCircle, Loader2, Lightbulb } from 'lucide-react';

type InputMode = 'select' | 'describe';

interface ScopeGatheringStepProps {
  onProjectTypeSelected: (projectType: ProjectType) => void;
  onDescriptionProvided: (description: string) => Promise<void>;
  address: string;
}

const projectTypeOptions = [
  { value: 'Kitchen' as ProjectType, icon: ChefHat, description: 'Kitchen remodel or renovation' },
  { value: 'Bathroom' as ProjectType, icon: Bath, description: 'Bathroom renovation or updates' },
  { value: 'Full House' as ProjectType, icon: Home, description: 'Whole home renovation' },
  { value: 'Basement' as ProjectType, icon: Building, description: 'Basement finishing or remodel' },
  { value: 'Custom' as ProjectType, icon: Wrench, description: 'Other renovation type' }
];

export const ScopeGatheringStep: React.FC<ScopeGatheringStepProps> = ({
  onProjectTypeSelected,
  onDescriptionProvided,
  address,
}) => {
  const [mode, setMode] = useState<InputMode>('describe');
  const [selectedProjectType, setSelectedProjectType] = useState<ProjectType | ''>('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleContinueWithSelection = () => {
    if (selectedProjectType) {
      onProjectTypeSelected(selectedProjectType);
    }
  };

  const handleContinueWithDescription = async () => {
    if (description.trim()) {
      setLoading(true);
      try {
        await onDescriptionProvided(description.trim());
      } catch (error) {
        console.error('Error analyzing description:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const canContinue = mode === 'select' ? !!selectedProjectType : description.trim().length > 0;

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-2xl">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Define Your Project Scope</h1>
        <p className="text-sm md:text-base text-gray-600">
          Tell us about the renovation you're planning at <strong>{address}</strong>
        </p>
      </div>

      <Card className="border-0 shadow-none">
        <CardHeader className="px-4 md:px-6">
          {/* Mode Toggle */}
          <div className="flex gap-2">
            <Button
              variant={mode === 'describe' ? 'default' : 'outline'}
              onClick={() => setMode('describe')}
              className="flex-1 text-sm md:text-base py-2 md:py-3"
            >
              <span className="hidden sm:inline">Describe Your Project</span>
              <span className="sm:hidden">Describe</span>
            </Button>
            <Button
              variant={mode === 'select' ? 'default' : 'outline'}
              onClick={() => setMode('select')}
              className="flex-1 text-sm md:text-base py-2 md:py-3"
            >
              Quick Select
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 px-4 md:px-6">
          {mode === 'describe' ? (
            <div className="space-y-3 md:space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs md:text-sm text-blue-900">
                    <p className="font-medium mb-1">Tell us about your project</p>
                    <p className="text-blue-700">
                      Examples: "I want to remodel my kitchen with new cabinets and countertops" or
                      "Looking to update my master bathroom, replace shower and vanity"
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Project Description *
                </label>
                <textarea
                  className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={5}
                  placeholder="Describe what you'd like to renovate... Include the rooms, specific changes you want, and any priorities you have."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs md:text-sm text-gray-500 mt-1">
                  {description.length} characters
                </p>
              </div>
            </div>
          ) : (
            <div className="grid gap-2 md:gap-3">
              {projectTypeOptions.map((option) => {
                const IconComponent = option.icon;
                const isSelected = selectedProjectType === option.value;

                return (
                  <div
                    key={option.value}
                    onClick={() => setSelectedProjectType(option.value)}
                    className={`p-3 md:p-4 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <IconComponent className={`h-5 w-5 md:h-6 md:w-6 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-grow min-w-0">
                        <h3 className="font-medium text-sm md:text-base">{option.value}</h3>
                        <p className="text-xs md:text-sm text-gray-600">{option.description}</p>
                      </div>
                      {isSelected && (
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {canContinue && (
            <div className="grid grid-cols-2 gap-2 md:gap-3 mt-4 md:mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => mode === 'select' ? setSelectedProjectType('') : setDescription('')}
                disabled={loading}
                className="text-sm md:text-base"
              >
                Clear
              </Button>
              <Button
                onClick={mode === 'select' ? handleContinueWithSelection : handleContinueWithDescription}
                disabled={loading}
                className="text-sm md:text-base"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 animate-spin" />
                    <span className="hidden sm:inline">Analyzing...</span>
                    <span className="sm:hidden">Wait...</span>
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-1 md:ml-2 h-3 w-3 md:h-4 md:w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};